import type { Theme } from '@goodtime/engine';
import { buildSlotAssets } from './slotAssets';

const ASSETS = buildSlotAssets('dungeons', {
  cherry:  'candle',
  lemon:   'rat',
  orange:  'key',
  bell:    'shackles',
  star:    'skeleton_wild',
  seven:   'torch',
  gem:     'padlock',
  scatter: 'vault_door_scatter',
  money:   'treasure_chest',
});

/** Castle Dungeons — the Kingdom narrative arc's captured beat.
 *  Sits between Wizard's Tower and Dragon's Hoard on the Kingdom
 *  map: after the wizard's intrigue but before the dragon
 *  encounter, the player is in the dungeons. Dim torchlight palette,
 *  chains-and-keys iconography.
 *
 *  <p><b>Mechanical personality — "Key Collector":</b>
 *  <ul>
 *    <li>Highest-variance slot in the Kingdom deck. The wheel has
 *        three zero segments (matching Dragon's Hoard) AND a x25
 *        jackpot — long droughts punctuated by big-vault cracks.
 *    <li>Padlock symbol (gem) at weight 2.5 — the key-collector
 *        mechanic relies on padlocks landing more often than other
 *        premiums to give the keys a chance to fill the bonus
 *        meter.
 *    <li>Scatter (vault_door) at weight 0.5 — the bonus is rare
 *        but pays out big when it triggers.
 *    <li>Money (treasure_chest) at 1.0 — the prize for surviving
 *        the dungeon.
 *  </ul>
 *  Intended feel: claustrophobic, high-tension. Long stretches of
 *  zero spins, then a vault cracks and the chest spills out. */
export const DUNGEONS_THEME: Theme = {
  id: 'dungeons',
  name: 'Castle Dungeons',
  palette: {
    accent: 0xe87b3a,         // torchlight orange
    secondary: 0x7a6b5d,      // stone grey
    warm: 0xffd08a,           // candle amber
    frameFill: 0x1f1a1f,      // dungeon dark
    bgGradientTop: 0x1c1820,  // dark stone
    bgGradientBottom: 0x0b080f, // deep dungeon
    dimOverlay: 0x05080d,
  },
  slot: {
    symbols: [
      // High-variance curve: padlock at 2.5 to give the key-collector
      // bonus a fair chance to fill; scatter at 0.5 (rarer than other
      // themes) so the vault bonus stays special when it triggers.
      // Commons (candle, rat) at 5 each — the dungeon teems with
      // little things.
      { id: 'cherry',  label: 'Candle',       glyph: '🕯️', tint: 0xffd08a, weight: 5, textureAlias: 'dungeons:cherry',
        winVideoAlias: 'dungeons:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Rat',          glyph: '🐀', tint: 0x7a6b5d, weight: 5, textureAlias: 'dungeons:lemon',
        winVideoAlias: 'dungeons:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Key',          glyph: '🗝️', tint: 0xe87b3a, weight: 4, textureAlias: 'dungeons:orange',
        winVideoAlias: 'dungeons:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Shackles',     glyph: '⛓️', tint: 0xc8ccd4, weight: 3, textureAlias: 'dungeons:bell',
        winVideoAlias: 'dungeons:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Skeleton',     glyph: '💀', tint: 0xfff2a8, weight: 2, textureAlias: 'dungeons:star', // wild
        winVideoAlias: 'dungeons:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Torch',        glyph: '🔥', tint: 0xe87b3a, weight: 2, textureAlias: 'dungeons:seven',
        winVideoAlias: 'dungeons:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Padlock',      glyph: '🔒', tint: 0xffd08a, weight: 2.5, textureAlias: 'dungeons:gem',
        winVideoAlias: 'dungeons:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Vault Door',   glyph: '🚪', tint: 0x7a6b5d, weight: 0.5, textureAlias: 'dungeons:scatter',
        winVideoAlias: 'dungeons:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Treasure Chest', glyph: '🪙', tint: 0xe87b3a, weight: 1,
        textureAlias: 'dungeons:money',
        winVideoAlias: 'dungeons:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    // Volatile bimodal wheel — three zero segments + a x25 jackpot.
    // Matches Dragon's bust-or-vault pattern. The dungeon doesn't
    // give up its loot easily; when it does, the vault opens.
    segments: [
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x1f1a1f, multiplier: 0,  weight: 4 },
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0xe87b3a, multiplier: 1,  weight: 4 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0xffd08a, multiplier: 2,  weight: 3 },
      { id: 'x0-b',   kind: 'multiplier', label: '✕',   tint: 0x1f1a1f, multiplier: 0,  weight: 3 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0x7a6b5d, multiplier: 3,  weight: 3 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0xe87b3a, multiplier: 1,  weight: 3 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xffd08a, multiplier: 5,  weight: 2 },
      { id: 'x0-c',   kind: 'multiplier', label: '✕',   tint: 0x1f1a1f, multiplier: 0,  weight: 3 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0xffd08a, multiplier: 2,  weight: 2 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xe87b3a, multiplier: 10, weight: 2 },
      { id: 'x5-b',   kind: 'multiplier', label: '×5',  tint: 0xffd08a, multiplier: 5,  weight: 2 },
      { id: 'x25-a',  kind: 'multiplier', label: '×25', tint: 0xffd08a, multiplier: 25, weight: 1 },
    ],
  },
  assets: {
    slot: ASSETS.slot,
    slotWin: ASSETS.slotWin,
    scene: {
      background:       { alias: 'dungeons:bg',         path: 'environment/dungeons.png' },
      columnBackground: { alias: 'dungeons:column_bg',  path: 'reel/dungeons_column_bg.png' },
      border:           { alias: 'dungeons:border',     path: 'brand/border_forest.png' },
    },
  },
};
